import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import $ from "jquery"; // Assuming jQuery is globally available via Webpacker

const Flash = ({
  notice: initialNotice,
  alert: initialAlert,
  timeout,
  logged_in,
}) => {
  const initialMessagePresent = !!(initialNotice || initialAlert);
  const [isOpen, setIsOpen] = useState(initialMessagePresent);
  const [timeLeft, setTimeLeft] = useState(timeout);
  const [currentNotice, setCurrentNotice] = useState(initialNotice);
  const [currentAlert, setCurrentAlert] = useState(initialAlert);
  const [buttonShow, setButtonShow] = useState(false);
  const intervalRef = useRef(null);

  const refreshPage = () => {
    window.location.reload(false);
  };

  const countDown = () => {
    setTimeLeft((prevTime) => {
      if (prevTime > 0) {
        if (prevTime <= 60 && !buttonShow) {
          // Show button only once when time is low
          // Clear existing alerts/notices before showing the timeout warning
          setCurrentNotice(null);
          setCurrentAlert(null);
          setIsOpen(true);
          setButtonShow(true);
        }
        return prevTime - 1;
      } else {
        // Time is up, log out
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        $.ajax({
          url: "/users/sign_out",
          type: "delete",
          dataType: "json",
          success: refreshPage,
          error: () => {
            // Add basic error handling
            console.error("Failed to sign out user.");
            refreshPage(); // Still try to refresh
          },
        });
        return 0; // Keep time at 0
      }
    });
  };

  const startTimeoutTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current); // Clear existing interval if any
    }
    intervalRef.current = setInterval(countDown, 1000);
  };

  // Effect to start timer on mount if logged in
  useEffect(() => {
    if (logged_in) {
      startTimeoutTimer();
    }
    // Cleanup function to clear interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [logged_in]); // Rerun effect only if logged_in status changes

  // Update state if initial props change (e.g., after form submission)
  useEffect(() => {
    setCurrentNotice(initialNotice);
    setCurrentAlert(initialAlert);
    setIsOpen(!!(initialNotice || initialAlert) || buttonShow); // Stay open if button is showing
  }, [initialNotice, initialAlert]);

  const closeMessage = () => {
    // Don't close if the timeout button is showing
    if (!buttonShow) {
      setIsOpen(false);
      // Optionally clear the message content when closed manually
      setCurrentNotice(null);
      setCurrentAlert(null);
    }
  };

  const resetCallback = () => {
    setIsOpen(false);
    setButtonShow(false);
    setTimeLeft(timeout); // Reset timer
    startTimeoutTimer(); // Restart the timer
  };

  const stayLoggedIn = () => {
    $.get("/admin/reset_user_clock", resetCallback).fail(() => {
      console.error("Failed to reset user clock via /admin/reset_user_clock");
      // Optionally attempt to reset locally anyway or show an error
      resetCallback();
    });
  };

  let flashState = "flash-message";
  if (isOpen) {
    flashState += " flash-message-open";
  }
  if (currentNotice) {
    flashState += " flash-message-notice";
  } else if (currentAlert) {
    flashState += " flash-message-alert";
  } else if (buttonShow) {
    flashState += " flash-message-alert"; // Style timeout warning as alert
  }

  const buttonClass = `pure-button admin-alert-button ${
    buttonShow ? "visable" : ""
  }`;

  // Don't render anything if not open and button not showing
  if (!isOpen && !buttonShow) {
    return null;
  }

  return (
    <div>
      <div className={flashState}>
        {/* Render close button only for regular notices/alerts */}
        {!buttonShow && (currentNotice || currentAlert) && (
          <div
            onClick={closeMessage}
            className="fa fa-times flash-close"
            id="flash-close"
          ></div>
        )}
        {currentNotice && <div className="notice">{currentNotice}</div>}
        {currentAlert && <div className="alert">{currentAlert}</div>}
        {/* Conditionally render the button element itself */}
        {buttonShow && (
          <button
            onClick={stayLoggedIn}
            className={buttonClass}
            // No longer need inline style if we only render when buttonShow is true
          >
            You will be logged out in {timeLeft} seconds due to inactivity.
            Click here to stay logged in.
          </button>
        )}
      </div>
    </div>
  );
};

Flash.propTypes = {
  notice: PropTypes.string,
  alert: PropTypes.string,
  timeout: PropTypes.number.isRequired,
  logged_in: PropTypes.bool.isRequired,
};

Flash.defaultProps = {
  notice: null,
  alert: null,
};

export default Flash;
